import API from '@aws-amplify/api';
import Constant from '@/common/constant/Constant';
import Logger from '@/common/utils/Logger';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { queryTranslationJobByS3Url } from '@/graphql/queries';
import { invokeTranslationUpdateApi } from '@/service/translationService';
import {
    hashS3Key,
    mergeJobsIntoLangStatusMap,
    normalizeS3Key
} from '@/common/utils/translationJobUtils';

const TAG = 'fileTranslationService';

const API_ERROR = Constant.TRANSLATION_API_ERROR_CODE;

// GSI3 page size. The query is paged to the end because one file accumulates a job per translate
// click, and missing an old page would hide a translation that still exists on the server.
const JOB_PAGE_SIZE = 200;
const MAX_JOB_PAGE = 20;

const buildIdentity = () => ({
    affiliationOrganization: localStorageUtil.getAffiliationOrganization(),
    projectInuse: localStorageUtil.getSelectedProjectId(),
    userId: localStorageUtil.getUserDetailId()
});

const isAttachmentJob = job => job?.requestSource === Constant.TRANSLATION_REQUEST_SOURCE.ATTACHMENT;

/**
 * Every translation job ever started for one source file, collapsed per language.
 *
 * @param {Object} params
 * @param {string} params.fileUrl                 raw attachment S3 key, without the public/ prefix
 * @param {string} params.affiliationOrganization organization OWNING the ticket, not the viewer's
 * @returns {Promise<{data: {langStatusMap: Object, sourceS3Url: string}|null, errorCode: string|null}>}
 */
export const getLangStatusMap = async ({ fileUrl, affiliationOrganization }) => {
    if (!fileUrl) return { data: null, errorCode: API_ERROR.INVALID_PARAM };

    const sourceS3Url = normalizeS3Key(fileUrl);

    try {
        let items = [];
        let nextToken = null;
        let page = 0;

        do {
            const res = await API.graphql({
                query: queryTranslationJobByS3Url,
                variables: {
                    sourceS3UrlHash: hashS3Key(sourceS3Url),
                    sortDirection: 'DESC',
                    limit: JOB_PAGE_SIZE,
                    nextToken
                }
            });

            const data = res?.data?.queryTranslationJobByS3Url;
            items.push(...(data?.items || []));
            nextToken = data?.nextToken || null;
            page += 1;
        } while (nextToken && page < MAX_JOB_PAGE);

        items = items.filter(i => isAttachmentJob(i));

        return {
            data: {
                langStatusMap: mergeJobsIntoLangStatusMap(items, affiliationOrganization),
                sourceS3Url
            },
            errorCode: null
        };
    } catch (err) {
        Logger.error(`${TAG} getLangStatusMap`, err);
        return { data: null, errorCode: API_ERROR.INTERNAL_ERROR };
    }
};

/**
 * @returns {Promise<{data: {jobId, sourceS3Url, sourceS3UrlHash, accepted: string[],
 *                          rejected: {lang: string, reason: string}[]}|null, errorCode: string|null}>}
 */
export const startTranslateFile = async ({ fileUrl, fileName, fileSize, ticketId, sourceLang, targetLangs }) => {
    if (!fileUrl || !fileName || !ticketId || !(targetLangs || []).length) {
        return { data: null, errorCode: API_ERROR.INVALID_PARAM };
    }

    const { affiliationOrganization, projectInuse, userId } = buildIdentity();

    return await invokeTranslationUpdateApi({
        affiliationOrganization,
        projectInuse,
        userId,
        actionType: Constant.TRANSLATION_ACTION.TRANSLATE_FILE_ATTACHMENT,
        // fileUrl goes up RAW: the Lambda adds the public/ prefix itself, and sending it prefixed
        // would produce public/public/... and a hash that matches nothing.
        requestBody: { fileUrl, fileName, fileSize, ticketId, sourceLang, targetLangs }
    });
};

/**
 * @returns {Promise<{data: {url}|null, errorCode: string|null}>}
 */
export const getTranslatedFileUrl = async ({ jobId, lang, ticketId }) => {
    if (!jobId || !lang || !ticketId) {
        return { data: null, errorCode: API_ERROR.INVALID_PARAM };
    }

    const { affiliationOrganization, projectInuse, userId } = buildIdentity();

    return await invokeTranslationUpdateApi({
        affiliationOrganization,
        projectInuse,
        userId,
        actionType: Constant.TRANSLATION_ACTION.GET_FILE_TRANSLATION_URL,
        // ticketId is what lets the Lambda check the caller may view the content this file belongs to.
        requestBody: { jobId, lang, ticketId }
    });
};

const fileTranslationService = {
    getLangStatusMap,
    startTranslateFile,
    getTranslatedFileUrl
};

export default fileTranslationService;
